import { useEffect, useMemo, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MagnifyingGlass, ArrowsLeftRight } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Route traffic: remap clicks from one brand_id (network A) to another
 * (network B) for one or many publishers. Old tracker URLs keep working.
 */
export function RouteTrafficDialog({
  open,
  onClose,
  sourcePublisherId,
  sourcePublisherEmail,
  fromBrand,
  assignedBrands = [],
  onDone,
}) {
  const [toBrandId, setToBrandId] = useState("");
  const [reason, setReason] = useState("");
  const [ensureAssign, setEnsureAssign] = useState(true);
  const [includeSiblings, setIncludeSiblings] = useState(true);
  const [allPubs, setAllPubs] = useState([]);
  const [loadingPubs, setLoadingPubs] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [candidateQ, setCandidateQ] = useState("");
  const [catalogHits, setCatalogHits] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  const fromId = fromBrand?.brand_id || "";
  const assignedIds = useMemo(
    () => new Set((assignedBrands || []).map((b) => b.brand_id).filter(Boolean)),
    [assignedBrands],
  );

  useEffect(() => {
    if (!open) return;
    setToBrandId("");
    setReason("");
    setEnsureAssign(true);
    setIncludeSiblings(true);
    setQ("");
    setCandidateQ("");
    setCatalogHits([]);
    const initial = new Set();
    if (sourcePublisherId) initial.add(sourcePublisherId);
    setSelected(initial);
    let cancelled = false;
    (async () => {
      setLoadingPubs(true);
      try {
        const { data } = await API.get("/admin/coverage-matrix");
        if (!cancelled) setAllPubs(data.publishers || []);
      } catch {
        toast.error("Failed to load publishers");
      } finally {
        if (!cancelled) setLoadingPubs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, sourcePublisherId, fromId]);

  // Catalog search — targets do not have to be assigned already
  // (Auto-assign creates the row). The assignment-only list hid ids
  // like inm_manual_rugsusa-com_2d6c9e when this pub did not have them.
  useEffect(() => {
    if (!open) return;
    const typed = candidateQ.trim() || toBrandId.trim();
    const q = typed || (fromBrand?.brand_name || "").trim();
    if (q.length < 3) {
      setCatalogHits([]);
      setCatalogLoading(false);
      return undefined;
    }
    setCatalogLoading(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await API.get("/admin/brands", {
          params: { q, hide_inactive: 1, limit: 40, fields: "lookup" },
        });
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : (data.items || []);
        setCatalogHits(
          rows
            .filter((b) => b.campaign_id && b.campaign_id !== fromId)
            .map((b) => ({
              brand_id: b.campaign_id,
              brand_name: b.name || b.advertiser_name || b.campaign_id,
              network: b.network || "",
              from_catalog: true,
            })),
        );
      } catch {
        if (!cancelled) setCatalogHits([]);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, candidateQ, toBrandId, fromId, fromBrand?.brand_name]);

  const toCandidates = useMemo(() => {
    const needle = (candidateQ.trim() || toBrandId.trim()).toLowerCase();
    const fromName = (fromBrand?.brand_name || "").toLowerCase();
    const assigned = (assignedBrands || [])
      .filter((b) => b.brand_id && b.brand_id !== fromId)
      .filter((b) => {
        if (!needle && fromName) {
          const n = (b.brand_name || "").toLowerCase();
          return n.includes(fromName.slice(0, 8)) || fromName.includes(n.slice(0, 8));
        }
        if (!needle) return true;
        return (
          (b.brand_name || "").toLowerCase().includes(needle)
          || (b.brand_id || "").toLowerCase().includes(needle)
          || (b.network || "").toLowerCase().includes(needle)
        );
      })
      .map((b) => ({ ...b, from_catalog: false }));

    const seen = new Set(assigned.map((b) => b.brand_id));
    const extra = catalogHits.filter((b) => {
      if (seen.has(b.brand_id)) return false;
      if (!needle) return true;
      return (
        (b.brand_name || "").toLowerCase().includes(needle)
        || (b.brand_id || "").toLowerCase().includes(needle)
        || (b.network || "").toLowerCase().includes(needle)
      );
    });
    const merged = [...extra, ...assigned];
    merged.sort((a, b) => {
      const aExact = (a.brand_id || "").toLowerCase() === needle ? 0 : 1;
      const bExact = (b.brand_id || "").toLowerCase() === needle ? 0 : 1;
      if (aExact !== bExact) return aExact - bExact;
      const aAssigned = assignedIds.has(a.brand_id) ? 0 : 1;
      const bAssigned = assignedIds.has(b.brand_id) ? 0 : 1;
      return aAssigned - bAssigned;
    });
    return merged.slice(0, 40);
  }, [assignedBrands, assignedIds, fromId, fromBrand, candidateQ, toBrandId, catalogHits]);

  const resolvedTo = useMemo(
    () => toCandidates.find((b) => b.brand_id === toBrandId.trim()) || null,
    [toCandidates, toBrandId],
  );

  const filteredPubs = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return allPubs
      .filter((p) => p.status !== "rejected" && p.status !== "pending_approval")
      .filter((p) => {
        if (!needle) return true;
        return (p.email || "").toLowerCase().includes(needle)
          || (p.id || "").toLowerCase().includes(needle);
      });
  }, [allPubs, q]);

  const togglePub = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (!fromId) {
      toast.error("Missing source brand");
      return;
    }
    if (!toBrandId.trim()) {
      toast.error("Pick a target brand");
      return;
    }
    if (selected.size === 0) {
      toast.error("Pick at least one publisher");
      return;
    }
    setSubmitting(true);
    try {
      const pubIds = Array.from(selected);
      let data;
      if (pubIds.length === 1) {
        // Single-publisher route — same path as per-pub CRUD (works even if bulk isn't deployed).
        const res = await API.post(
          `/admin/publishers/${pubIds[0]}/traffic-remaps`,
          {
            from_brand_id: fromId,
            to_brand_id: toBrandId.trim(),
            reason: reason.trim(),
            ensure_assign: ensureAssign,
            include_sibling_brands: includeSiblings,
          },
        );
        data = { ok: 1, errors: 0, results: [{ publisher_id: pubIds[0], status: "ok", remap: res.data?.remap }] };
      } else {
        const res = await API.post("/admin/traffic-remaps/bulk", {
          publisher_ids: pubIds,
          from_brand_id: fromId,
          to_brand_id: toBrandId.trim(),
          reason: reason.trim(),
          ensure_assign: ensureAssign,
          include_sibling_brands: includeSiblings,
        });
        data = res.data;
      }
      const ok = data.ok || 0;
      const errors = data.errors || 0;
      if (ok && !errors) {
        toast.success(`Routed traffic for ${ok} publisher${ok === 1 ? "" : "s"}`);
      } else if (ok) {
        toast.warning(`Routed ${ok}, failed ${errors} — check results`);
      } else {
        const first = (data.results || []).find((r) => r.status === "error");
        toast.error(first?.error || "Routing failed");
      }
      onDone?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Routing failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowsLeftRight size={18} />
            Route brand traffic
          </DialogTitle>
          <DialogDescription>
            Clicks on the source tracker URL will redirect via the target
            network brand. Select one or more publishers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">From</div>
            <div className="font-medium text-slate-900">{fromBrand?.brand_name || fromId}</div>
            <div className="text-xs text-slate-500 font-mono mt-0.5">
              {fromBrand?.network || "?"} · {fromId}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">To brand id</label>
            <Input
              className="mt-1 font-mono text-xs"
              placeholder="e.g. inm_manual_rugsusa-com_2d6c9e or 3773223"
              value={toBrandId}
              onChange={(e) => setToBrandId(e.target.value)}
              data-testid="route-traffic-to-brand"
            />
            {resolvedTo && (
              <div className="mt-1.5 text-xs text-slate-700">
                <span className="font-medium">{resolvedTo.brand_name}</span>
                <span className="text-slate-400 ml-1">{resolvedTo.network}</span>
                {!assignedIds.has(resolvedTo.brand_id) && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700">
                    not assigned{ensureAssign ? " — will auto-assign" : ""}
                  </span>
                )}
              </div>
            )}
            {toBrandId.trim() && !resolvedTo && !catalogLoading && (
              <div className="mt-1.5 text-[11px] text-amber-700">
                Not found in catalog or this pub’s assignments. Check the id.
              </div>
            )}
            <Input
              className="mt-2 h-8 text-xs"
              placeholder="Search catalog or this pub’s assignments…"
              value={candidateQ}
              onChange={(e) => setCandidateQ(e.target.value)}
              data-testid="route-traffic-candidate-filter"
            />
            <div className="mt-2 max-h-36 overflow-y-auto border border-slate-100 rounded-md divide-y">
              {catalogLoading && toCandidates.length === 0 && (
                <div className="px-2 py-2 text-xs text-slate-400">Searching catalog…</div>
              )}
              {!catalogLoading && toCandidates.length === 0 && (
                <div className="px-2 py-2 text-xs text-slate-400">
                  No candidates — paste a catalog brand_id above (it does not need to be assigned)
                </div>
              )}
              {toCandidates.map((b) => (
                <button
                  key={b.brand_id}
                  type="button"
                  className={`w-full text-left px-2 py-1.5 text-xs hover:bg-slate-50 ${toBrandId === b.brand_id ? "bg-sky-50" : ""}`}
                  onClick={() => setToBrandId(b.brand_id)}
                >
                  <span className="font-medium">{b.brand_name}</span>
                  <span className="text-slate-400 ml-2">{b.network}</span>
                  {!assignedIds.has(b.brand_id) && (
                    <span className="ml-2 text-[9px] uppercase tracking-wider text-amber-600">catalog</span>
                  )}
                  <span className="block font-mono text-[10px] text-slate-400">{b.brand_id}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Reason (optional)</label>
            <Input className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="ops: route Intango SHEIN to CJ" />
          </div>

          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={includeSiblings} onChange={(e) => setIncludeSiblings(e.target.checked)} />
              Also route all same-merchant brand IDs on this network (e.g. all Shopnomix SHEIN locales)
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={ensureAssign} onChange={(e) => setEnsureAssign(e.target.checked)} />
              Auto-assign target brand if missing
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                Publishers ({selected.size} selected)
              </label>
              <div className="flex gap-2">
                <button type="button" className="text-[11px] text-sky-700" onClick={() => setSelected(new Set(filteredPubs.map((p) => p.id)))}>
                  Select filtered
                </button>
                <button type="button" className="text-[11px] text-slate-500" onClick={() => setSelected(new Set(sourcePublisherId ? [sourcePublisherId] : []))}>
                  Reset
                </button>
              </div>
            </div>
            <div className="relative mb-2">
              <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-slate-400" />
              <Input className="pl-7 h-8 text-xs" placeholder="Search publishers…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md">
              {loadingPubs && <div className="p-2 text-xs text-slate-400">Loading…</div>}
              {!loadingPubs && filteredPubs.map((p) => (
                <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-slate-50 border-b border-slate-50 last:border-0">
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => togglePub(p.id)}
                  />
                  <span className="truncate">{p.email || p.id}</span>
                  {p.id === sourcePublisherId && (
                    <span className="text-[9px] uppercase text-sky-600 font-bold">current</span>
                  )}
                </label>
              ))}
            </div>
            {sourcePublisherEmail && (
              <p className="text-[11px] text-slate-400 mt-1">Opened from {sourcePublisherEmail}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="route-traffic-submit">
            {submitting ? "Routing…" : "Apply routing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
